/**
 * GID-106: "switching sessions cancels query".
 * GID-107: "response bled into another project/session".
 *
 * An in-flight streaming query is bound to the chat session it was sent from.
 * If the user switches sessions (or projects) mid-answer, the request is NOT
 * aborted (the backend completes it and persists the answer to its origin
 * session), but the frontend must stop rendering that stream into whatever
 * session/project is now in view — otherwise the live element is wiped by the
 * switch re-render and the remaining chunks bleed into the wrong place.
 *
 * The origin MUST be bound explicitly, at send time, via bindOrigin() — not
 * read lazily from a live "current session" getter on the first stream chunk.
 * The send path (chatService.sendMessage) awaits project-context work between
 * ensuring the session and actually dispatching the stream; a project/session
 * switch during that gap can null or redirect the live session before the
 * first chunk ever arrives. A lazy read at that point sees the POST-switch
 * value (null, or a foreign session) instead of the TRUE origin, defeating
 * the guard. Binding eagerly — with the value chatService already guarantees
 * non-null right after it ensures the session — closes that race entirely.
 *
 * This guard is pure / DOM-free so it can be unit-tested. taskpane wires the
 * DOM side (updateStreamingMessage / finalizeStreamingMessage / addMessage).
 *
 * @param {() => (string|null)} getCurrentSessionId - reads the currently-viewed session id
 */
export function createStreamSessionGuard(getCurrentSessionId) {
  let origin = null;

  return {
    /**
     * Bind the origin session ONCE, synchronously, at send time. Must be
     * called with the session id the request is actually dispatched under
     * (chatService's onSessionBound callback), before any await can yield
     * control to a switch.
     */
    bindOrigin(sessionId) {
      if (origin == null) origin = sessionId;
    },

    /** The bound origin session id (null until bindOrigin is called). */
    originSessionId() {
      return origin;
    },

    /**
     * Was an origin ever bound? False means the request never reached
     * dispatch (e.g. it threw while ensuring the session, before
     * onSessionBound could fire) — there is no "wrong session" to guard
     * against, since nothing was ever sent from a session to switch away
     * from. Callers that only run on a genuine failure-to-send path (a
     * caught error with no chunks/completion) should check this before
     * shouldRender(), so a pre-dispatch failure isn't mistaken for "user
     * switched away" and silently swallowed.
     */
    isBound() {
      return origin != null;
    },

    /**
     * Should this stream's output render into the currently-viewed session?
     *
     * @param {string|null} [target] - explicit owner (e.g. onComplete's
     *   data.session_id); falls back to the bound origin.
     * @returns {boolean} true only when there is a known owner AND the user
     *   is still viewing it. An unknown owner (bindOrigin not yet called)
     *   does NOT render — a dispatched stream always has a bound origin, so
     *   this only guards the (should-be-unreachable) case where a chunk
     *   arrives before binding completed. Callers that can run BEFORE any
     *   origin is ever bound (e.g. a pre-dispatch error) must check
     *   isBound() first — see above.
     */
    shouldRender(target) {
      const owner = target != null ? target : origin;
      return owner != null && getCurrentSessionId() === owner;
    },
  };
}
